/**
 * Build-time sanity checks on `VITE_API_BASE_URL`.
 *
 * Pure module — no `import.meta.env`, no Vite — so `vite.config.ts` can run it
 * at config time and the Vitest suite can pin it. It exists because of a real
 * incident (2026-09-16): a git-ignored `.env.production.local`, left behind by
 * a PWA verification build, pinned the API base URL to `http://localhost:4173`.
 * Vite loads `.env.[mode].local` ABOVE `.env.[mode]`, and `npm run publish` is
 * just `vite build` in `production` mode, so the file silently overrode
 * `https://dash.erpat.app` for every build — including the one deployed to
 * syntry.web.app, whose bundle (and service-worker cache routes) then called
 * the visitor's own machine and reported a "network or connection issue".
 *
 * A production bundle whose API origin is a loopback address can never work
 * for anyone but the developer who built it, so the build refuses to produce
 * one. The local-preview verification recipe has its own Vite mode instead
 * (`npm run build:verify` → `.env.verify`), which this guard does not touch.
 */

/** Hostnames that only ever resolve to the machine running the browser. */
const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '0.0.0.0', '::']);

/**
 * True when `hostname` can only reach the local machine: `localhost` and its
 * `*.localhost` subdomains, the IPv4/IPv6 loopback and unspecified addresses,
 * and the whole `127.0.0.0/8` block. `new URL().hostname` keeps the brackets
 * on an IPv6 literal (`[::1]`), so those are stripped before comparing.
 */
export function isLoopbackHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/^\[|\]$/g, '');
  if (host === '') return false;
  if (LOOPBACK_HOSTNAMES.has(host)) return true;
  if (host.endsWith('.localhost')) return true;
  return /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host);
}

/** Why a value is not a deployable API base URL, or `null` when it is. */
export function deployableApiBaseUrlProblem(value: string | undefined): string | null {
  const url = (value ?? '').trim();
  if (url === '') return 'VITE_API_BASE_URL is not set';

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return `VITE_API_BASE_URL is not an absolute URL: "${url}"`;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return `VITE_API_BASE_URL must be an http(s) URL: "${url}"`;
  }
  if (isLoopbackHostname(parsed.hostname)) {
    return `VITE_API_BASE_URL points at the local machine: "${url}"`;
  }
  return null;
}

/**
 * Throw unless `value` is an API base URL a deployed bundle could actually
 * reach. Call this for a production-mode `vite build` only — dev, test and
 * the `verify` preview mode legitimately use a same-origin localhost URL that
 * the Vite server proxies to the backend.
 */
export function assertDeployableApiBaseUrl(value: string | undefined): void {
  const problem = deployableApiBaseUrlProblem(value);
  if (problem === null) return;

  throw new Error(
    [
      `[syntry] Refusing to build a production bundle: ${problem}.`,
      'The deployed PWA and the native shells would call that origin from every',
      "visitor's device. Check for a stray .env.production.local (Vite loads it",
      'above .env.production) and set VITE_API_BASE_URL to the public ERPat host,',
      'e.g. https://dash.erpat.app. For a local preview-server verification build',
      'use `npm run build:verify` (the `verify` Vite mode, see .env.verify).',
    ].join('\n'),
  );
}
